import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, 
  AlertCircle, 
  Calendar, 
  Clock, 
  User, 
  File, 
  Info,
  MessageSquare,
  Filter,
  Share,
  Download,
  Printer,
  Eye,
  MoreHorizontal,
  ChevronRight,
  Timer,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Check,
  X,
  CheckCircle,
} from 'lucide-react';

import completedSurveyService from '../../services/completedSurveyService';
import Tooltip from '../Shared/Tooltip';
import { formatDate, formatDateTime, calculateDuration } from '../../utils/dateUtils';
import { SuspendedBadge } from '../common/ParticipantStatusBadge';

const OPTIONAL_NO_RESPONSE_TOOLTIP = 'This question was optional. The participant did not provide a response.';

// Helper function to format duration from seconds
const formatDurationFromSeconds = (seconds) => {
  if (!seconds || seconds < 0) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

// Helper function to determine question type based on answer format
function determineQuestionType(answerValue) {
  if (typeof answerValue === 'number') {
    return 'numeric';
  } else if (answerValue === 'true' || answerValue === 'false' || answerValue === true || answerValue === false) {
    return 'yes_no';
  } else if (answerValue && typeof answerValue === 'string' && answerValue.includes('-') && answerValue.split('-').length === 3) {
    // Simple pattern matching for dates (YYYY-MM-DD)
    return 'date';
  }
  
  return 'text';
}

// Helper function to format question type for display
function formatQuestionType(type) {
  if (!type) return 'Unknown';
  
  // Map of backend types to display labels
  const typeMap = {
    'text': 'Text',
    'numeric': 'Numeric',
    'date': 'Date',
    'yes_no': 'Yes/No',
    'multiple_choice': 'Multiple Choice',
    'checkbox': 'Checkbox',
    'rating': 'Rating Scale',
    'likert': 'Ordinal',
    'email': 'Email',
    'time': 'Time',
    'datetime': 'Date & Time',
    'file': 'File Upload',
    'signature': 'Signature',
    'info': 'Information',
    'ordinal': 'Ordinal',
    'nominal': 'Nominal',
    'boolean': 'Yes/No',
    'vas': 'Visual Analog Scale',
    'nrs': 'Numeric Rating Scale',
    'dropdown': 'Dropdown'
  };
  
  // If type exists in our map, return formatted version, otherwise capitalize first letter
  return typeMap[type.toLowerCase()] || 
    type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

const CompletedSurveyDetails = () => {
  const { participantID, studyId, siteId, surveyId } = useParams();
  const navigate = useNavigate();
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('all');
  const [showActions, setShowActions] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [participantHumanId, setParticipantHumanId] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  useEffect(() => {
    const fetchSurveyDetails = async () => {
      try {
        setLoading(true);
        console.log(`Fetching details for survey ID: ${surveyId}`);
        const data = await completedSurveyService.getCompletedSurveyDetails(surveyId);
        console.log('Received survey details:', data);
        
        // Clean up data before setting state
        // Remove email for privacy reasons
        if (data.participant_email) {
          delete data.participant_email;
        }
        
        setSurveyDetails(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching survey details:', err);
        const errorMessage = err.response?.data?.error || 'Failed to load survey details. Please try again later.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchSurveyDetails();
    }

    // Add scroll event listener
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [surveyId]);

  // No duplicate useEffect needed here
  
  // Log survey details when they change and extract participant ID
  useEffect(() => {
    if (surveyDetails) {
      console.log('Survey Details loaded:', surveyDetails);
      console.log('is_suspended value:', surveyDetails.is_suspended);
      console.log('status value:', surveyDetails.status);

      // PRIORITY 1: Get the human-readable participant ID from the API response (most reliable)
      if (surveyDetails.participant_human_id) {
        // Use the human-readable ID directly from the API response
        setParticipantHumanId(surveyDetails.participant_human_id);
        console.log('Using participant_human_id from API:', surveyDetails.participant_human_id);
        return; // Early return - we found it!
      } else if (surveyDetails.participant_details && surveyDetails.participant_details.participant_id) {
        // Fallback 1: From participant_details object (from SimpleParticipantSerializer)
        setParticipantHumanId(surveyDetails.participant_details.participant_id);
        console.log('Using participant_id from participant_details');
      } else if (surveyDetails.participant_email) {
        // Fallback 2: Extract from email if available (older format)
        const emailParts = surveyDetails.participant_email.split('@');
        if (emailParts.length > 0) {
          setParticipantHumanId(emailParts[0]); // Usually in format 1002@site.com
          console.log('Extracted participant ID from email');
        }
      } else if (surveyDetails.metadata && surveyDetails.metadata.participant_id) {
        // Fallback 3: Check metadata
        setParticipantHumanId(surveyDetails.metadata.participant_id);
        console.log('Using participant_id from metadata');
      } else {
        // Last resort: Show a message that we couldn't find the human-readable ID
        setParticipantHumanId('ID not available');
        console.warn('Could not find human-readable participant ID in response');
      }
    }
  }, [surveyDetails, participantID]); // Run when surveyDetails or participantID changes

  const handleBack = () => {
    // Route params when on the scoped URL; otherwise the external UUIDs from
    // the details API, so the unscoped /completed-surveys/:id entry point
    // (e.g. from a notification) still lands on the participant. Study/site
    // pages only resolve external UUIDs, so int ids are never used here.
    const s = studyId || surveyDetails?.study_external_id;
    const si = siteId || surveyDetails?.site_external_id;
    const p = participantID || surveyDetails?.participant_id;
    if (s && si && p) {
      navigate(`/studies/${s}/sites/${si}/participant/${p}`);
    } else {
      window.history.back();
    }
  };

  // Filter questions based on active section
  const getFilteredQuestions = () => {
    // First check if we have questions array
    if (!surveyDetails?.questions || surveyDetails.questions.length === 0) {
      if (surveyDetails?.responses && Object.keys(surveyDetails.responses).length > 0) {
        return [];
      }
      return [];
    }
    
    if (activeSection === 'all') {
      return surveyDetails.questions || [];
    }

    return surveyDetails.questions.filter(question => {
      switch (activeSection) {
        case 'text':
          return ['text', 'email'].includes(question.type);
        case 'choice':
          return ['multiple_choice', 'checkbox', 'yes_no', 'nominal', 'ordinal', 'boolean', 'dropdown'].includes(question.type);
        case 'numeric':
          return ['rating', 'numeric', 'vas', 'nrs', 'likert'].includes(question.type);
        case 'date':
          return ['date'].includes(question.type);
        default:
          return true;
      }
    });
  };

  // Get icon for question type
  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'text':
      case 'email':
        return <MessageSquare className="w-4 h-4" />;
      case 'multiple_choice':
      case 'checkbox':
      case 'nominal':
      case 'ordinal':
      case 'dropdown':
        return <CheckCircle className="w-4 h-4" />;
      case 'yes_no':
      case 'boolean':
        return <Check className="w-4 h-4" />;
      case 'rating':
      case 'numeric':
      case 'nrs':
      case 'vas':
      case 'likert':
        return <Eye className="w-4 h-4" />;
      case 'date':
        return <Calendar className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  // Get color for question type
  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'text':
        return 'bg-blue-50 text-blue-600';
      case 'email':
        return 'bg-sky-50 text-sky-600';
      case 'multiple_choice':
        return 'bg-purple-50 text-purple-600';
      case 'checkbox':
        return 'bg-fuchsia-50 text-fuchsia-600';
      case 'nominal':
        return 'bg-pink-50 text-pink-600';
      case 'dropdown':
        return 'bg-violet-50 text-violet-600';
      case 'yes_no':
      case 'boolean':
        return 'bg-primary-50 text-primary-600';
      case 'rating':
        return 'bg-amber-50 text-amber-600';
      case 'numeric':
        return 'bg-yellow-50 text-yellow-700';
      case 'nrs':
        return 'bg-orange-50 text-orange-600';
      case 'vas':
        return 'bg-lime-50 text-lime-700';
      case 'likert':
        return 'bg-rose-50 text-rose-600';
      case 'ordinal':
        return 'bg-cyan-50 text-cyan-600';
      case 'date':
        return 'bg-indigo-50 text-indigo-600';
      default:
        return 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300';
    }
  };

  // Get formatted answer value for the table view
  // Helper: resolve answer code to label using question options
  const resolveAnswerLabel = (answer, options) => {
    if (!options || answer === null || answer === undefined) return answer;
    if (Array.isArray(options)) {
      for (const opt of options) {
        if (typeof opt === 'object') {
          if (String(opt.value) === String(answer) || String(opt.label) === String(answer)) {
            return opt.label || opt.value;
          }
        }
      }
    }
    return answer;
  };

  const getFormattedTableAnswer = (question) => {
    const { type, answer, options } = question;
    
    if (answer === null || answer === undefined) {
      // Check if this is a partial/suspended survey
      if (surveyDetails?.is_suspended && surveyDetails?.has_partial_responses) {
        return (
          <span className="text-gray-400 dark:text-gray-500 italic">
            Not answered (participant deactivated)
          </span>
        );
      }
      if (question.is_required === false) {
        return (
          <Tooltip content={OPTIONAL_NO_RESPONSE_TOOLTIP} position="top" showIcon={false}>
            <span className="text-gray-400 dark:text-gray-500 italic">No response (optional)</span>
          </Tooltip>
        );
      }
      return 'No answer provided';
    }

    let displayAnswer = answer;
    
    if (answer && typeof answer === 'object' && answer.answer !== undefined) {
      displayAnswer = answer.answer;
    }
    
    switch (type) {
      case 'yes_no':
      case 'boolean': {
        const isYes = displayAnswer === 'Yes' || displayAnswer === 'yes' || displayAnswer === true || displayAnswer === 1 || displayAnswer === '1';
        const isNo = displayAnswer === 'No' || displayAnswer === 'no' || displayAnswer === false || displayAnswer === 0 || displayAnswer === '0';
        // Handle dict options (stored as {type:'boolean', yesLabel:'...', noLabel:'...'})
        if (options && !Array.isArray(options) && typeof options === 'object') {
          if (isYes) return options.yesLabel || 'Yes';
          if (isNo) return options.noLabel || 'No';
        }
        if (isYes && options && Array.isArray(options) && options[0]) return typeof options[0] === 'object' ? (options[0].label || 'Yes') : 'Yes';
        if (isNo && options && Array.isArray(options) && options[1]) return typeof options[1] === 'object' ? (options[1].label || 'No') : 'No';
        return isYes ? 'Yes' : isNo ? 'No' : String(displayAnswer);
      }
      
      case 'rating':
      case 'numeric':
      case 'nrs':
        return String(displayAnswer);

      case 'vas':
        return typeof displayAnswer === 'number' ? `${displayAnswer.toFixed(1)}%` : String(displayAnswer);
        
      case 'date':
        return formatDate(displayAnswer);
        
      case 'multiple_choice':
      case 'nominal':
      case 'ordinal':
      case 'likert':
      case 'dropdown':
      case 'checkbox':
        if (Array.isArray(displayAnswer)) {
          return displayAnswer.map(a => resolveAnswerLabel(a, options)).join(', ');
        }
        return String(resolveAnswerLabel(displayAnswer, options));
        
      default:
        return typeof displayAnswer === 'object' ? JSON.stringify(displayAnswer) : String(displayAnswer);
    }
  };

  // Get detailed answer component for expanded view
  const renderDetailedAnswer = (question) => {
    // Instead of destructuring, get the properties directly
    const { id, type, answer } = question;
    
    if (answer === null || answer === undefined) {
      if (question.is_required === false) {
        return (
          <Tooltip content={OPTIONAL_NO_RESPONSE_TOOLTIP} position="top" showIcon={false}>
            <div className="text-gray-400 dark:text-gray-500 italic mt-2">No response (optional)</div>
          </Tooltip>
        );
      }
      return (
        <div className="text-gray-500 dark:text-gray-400 italic mt-2">No answer provided</div>
      );
    }
    
    // Extract timestamp and actual answer from the answer object
    let timestamp = null;
    let displayAnswer = answer;
    
    // Check if answer has timestamp directly
    if (answer && typeof answer === 'object') {
      // If answer has a timestamp property, create the timestamp element
      if (answer.timestamp) {
        timestamp = (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            <span>Answered at: {formatDateTime(answer.timestamp)}</span>
          </div>
        );
      }
      
      // If answer has an answer property, use it as the display answer
      if (answer.answer !== undefined) {
        displayAnswer = answer.answer;
      }
    }

    switch (type) {
      case 'text':
      case 'email':
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
            {displayAnswer}
            {timestamp}
          </div>
        );
      
      case 'multiple_choice':
        // Check if options array exists to create a better UI
        if (question.options && Array.isArray(question.options)) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {(() => {
                  // Match only the first option carrying the saved value/label —
                  // duplicate values are allowed and can't be disambiguated from the
                  // stored answer, so highlight a single option (FECOA-1521).
                  const selectedIdx = question.options.findIndex((option) => {
                    const oLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                    const oValue = typeof option === 'object' ? (option.value !== undefined ? option.value : option.label) : option;
                    return String(oValue) === String(displayAnswer) || oLabel === displayAnswer;
                  });
                  return question.options.map((option, idx) => {
                  // Handle both string options and {label, value} object options
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const isSelected = idx === selectedIdx;

                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      <div className={`h-3 w-3 rounded-full mr-3 flex-shrink-0 ${isSelected ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                      <span className={isSelected ? 'font-medium' : ''}>{optionLabel}</span>
                      {isSelected && <Check className="w-4 h-4 text-purple-500 ml-auto" />}
                    </div>
                  );
                  });
                })()}
              </div>
              {timestamp}
            </div>
          );
        }

        // Fallback if no options array - handle object displayAnswer
        const displayAnswerText = typeof displayAnswer === 'object' 
          ? (displayAnswer.label || displayAnswer.value || JSON.stringify(displayAnswer))
          : displayAnswer;
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-purple-50 border border-purple-200">
              <div className="h-3 w-3 rounded-full bg-purple-500 mr-3"></div>
              <span className="font-medium">{displayAnswerText}</span>
            </div>
            {timestamp}
          </div>
        );
      
      case 'checkbox':
        if (Array.isArray(displayAnswer)) {
          return (
            <div className="space-y-2">
              {displayAnswer.map((item, idx) => (
                <div key={idx} className="flex items-center bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
                  <Check className="w-4 h-4 text-primary-500 mr-2" />
                  <span className="text-gray-800 dark:text-gray-100">{item}</span>
                </div>
              ))}
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 flex items-center text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            {String(displayAnswer) === 'true' ? (
              <Check className="w-4 h-4 text-green-500 mr-2" />
            ) : (
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
            )}
            {String(displayAnswer)}
            {timestamp}
          </div>
        );
      
      case 'rating':
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center mb-2">
              <span className="bg-amber-100 text-amber-800 text-lg font-medium rounded-md px-3 py-1 mr-2">
                {displayAnswer}
              </span>
              {question.options?.min_value && question.options?.max_value && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  (Scale: {question.options.min_value} to {question.options.max_value})
                </span>
              )}
            </div>
            
            {question.options?.min_value && question.options?.max_value && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 bg-amber-500 rounded-full" 
                  style={{ 
                    width: `${((displayAnswer - question.options.min_value) / (question.options.max_value - question.options.min_value)) * 100}%` 
                  }}
                ></div>
              </div>
            )}
            {timestamp}
          </div>
        );
      
      case 'likert':
        // Resolve numeric answer to label using question.options array
        const ordinalOptions = question.options && Array.isArray(question.options) ? question.options : [];
        let selectedLabel = String(displayAnswer);
        let selectedIdx = -1;
        // Match only the first option carrying the saved value — duplicate values
        // are allowed and the stored answer can't disambiguate beyond the value
        // itself, so highlight a single option rather than all matches (FECOA-1521).
        ordinalOptions.forEach((opt, idx) => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          if (selectedIdx === -1 && String(optValue) === String(displayAnswer)) {
            selectedLabel = typeof opt === 'object' ? (opt.label || String(opt.value)) : String(opt);
            selectedIdx = idx;
          }
        });
        
        if (ordinalOptions.length > 0) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {ordinalOptions.map((option, idx) => {
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const isSelected = idx === selectedIdx;
                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? 'bg-cyan-50 border border-cyan-200' : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      <div className={`h-3 w-3 rounded-full mr-3 flex-shrink-0 ${isSelected ? 'bg-cyan-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                      <span className={isSelected ? 'font-medium' : ''}>{optionLabel}</span>
                      {isSelected && <Check className="w-4 h-4 text-cyan-500 ml-auto" />}
                    </div>
                  );
                })}
              </div>
              {selectedIdx !== -1 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 bg-cyan-500 rounded-full" 
                      style={{ width: `${(selectedIdx / (ordinalOptions.length - 1)) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{typeof ordinalOptions[0] === 'object' ? ordinalOptions[0].label : ordinalOptions[0]}</span>
                    <span>{typeof ordinalOptions[ordinalOptions.length - 1] === 'object' ? ordinalOptions[ordinalOptions.length - 1].label : ordinalOptions[ordinalOptions.length - 1]}</span>
                  </div>
                </div>
              )}
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="font-medium text-gray-800 dark:text-gray-100">{selectedLabel}</div>
            {timestamp}
          </div>
        );
      
      case 'yes_no':
      case 'boolean': {
        // Handle all variations of Yes/No answers including numeric 1/0
        const isYesDetailed = displayAnswer === 'Yes' || displayAnswer === 'yes' || displayAnswer === true || displayAnswer === 1 || displayAnswer === '1';
        const isNoDetailed = displayAnswer === 'No' || displayAnswer === 'no' || displayAnswer === false || displayAnswer === 0 || displayAnswer === '0';
        // Resolve custom label from dict options (e.g. {type:'boolean', yesLabel:'Yes sir', noLabel:'No sir'})
        let boolLabel = String(displayAnswer);
        if (question.options && !Array.isArray(question.options) && typeof question.options === 'object') {
          if (isYesDetailed) boolLabel = question.options.yesLabel || 'Yes';
          else if (isNoDetailed) boolLabel = question.options.noLabel || 'No';
        } else if (question.options && Array.isArray(question.options)) {
          if (isYesDetailed && question.options[0]) boolLabel = typeof question.options[0] === 'object' ? (question.options[0].label || 'Yes') : 'Yes';
          else if (isNoDetailed && question.options[1]) boolLabel = typeof question.options[1] === 'object' ? (question.options[1].label || 'No') : 'No';
        } else {
          boolLabel = isYesDetailed ? 'Yes' : isNoDetailed ? 'No' : String(displayAnswer);
        }
        
        return (
          <div>
            <div className={`inline-flex items-center px-4 py-2 rounded-md shadow-sm dark:shadow-gray-900/20 ${
              isYesDetailed ? 'bg-green-100 text-green-800 border border-green-200' : 
              isNoDetailed ? 'bg-red-100 text-red-800 border border-red-200' : 
              'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
            }`}>
              {isYesDetailed ? (
                <Check className="w-4 h-4 mr-2" />
              ) : isNoDetailed ? (
                <X className="w-4 h-4 mr-2" />
              ) : (
                <AlertCircle className="w-4 h-4 mr-2" />
              )}
              <span className="font-medium">{boolLabel}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      
      case 'numeric': {
        // Options use camelCase: minValue, maxValue, leftAnchor, rightAnchor, step
        const nMin = question.options?.minValue ?? question.options?.min_value ?? 0;
        const nMax = question.options?.maxValue ?? question.options?.max_value ?? 10;
        const nStep = question.options?.step ?? 1;
        const nLeftAnchor = question.options?.leftAnchor || question.options?.left_anchor || '';
        const nRightAnchor = question.options?.rightAnchor || question.options?.right_anchor || '';
        const nValue = typeof displayAnswer === 'number' ? displayAnswer : parseFloat(displayAnswer) || 0;
        const nScaleNumbers = [];
        for (let n = nMin; n <= nMax; n += nStep) {
          nScaleNumbers.push(n);
        }
        
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex flex-wrap gap-2 justify-center">
              {nScaleNumbers.map((num) => (
                <div
                  key={num}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    num === nValue
                      ? 'bg-yellow-500 text-white border-2 border-yellow-600'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {num}
                </div>
              ))}
            </div>
            {(nLeftAnchor || nRightAnchor) && (
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mt-2">
                <span>{nLeftAnchor}</span>
                <span>{nRightAnchor}</span>
              </div>
            )}
            {timestamp}
          </div>
        );
      }
      case 'vas': {
        // Options use camelCase: minValue, maxValue, leftAnchor, rightAnchor
        const vasMin = question.options?.minValue ?? question.options?.min_value ?? 0;
        const vasMax = question.options?.maxValue ?? question.options?.max_value ?? 100;
        const vasLeftAnchor = question.options?.leftAnchor || question.options?.left_anchor || '';
        const vasRightAnchor = question.options?.rightAnchor || question.options?.right_anchor || '';
        const vasValue = typeof displayAnswer === 'number' ? displayAnswer : parseFloat(displayAnswer) || 0;
        const vasPercent = ((vasValue - vasMin) / (vasMax - vasMin)) * 100;
        
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="text-center mb-3">
              <span className="bg-lime-100 text-lime-800 text-lg font-medium rounded-md px-3 py-1">
                {vasValue.toFixed(1)}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                / {vasMax}
              </span>
            </div>
            <div className="relative w-full h-3 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded-full">
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white dark:bg-gray-800 border-2 border-lime-600 rounded-full shadow-md dark:shadow-gray-900/20"
                style={{ left: `calc(${vasPercent}% - 10px)` }}
              ></div>
            </div>
            {(vasLeftAnchor || vasRightAnchor) && (
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mt-2">
                <span>{vasLeftAnchor}</span>
                <span>{vasRightAnchor}</span>
              </div>
            )}
            {timestamp}
          </div>
        );
      }
      case 'date':
        return (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 rounded-md border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center mb-2">
              <Calendar className="w-4 h-4 text-indigo-500 mr-2" />
              <span className="text-gray-800 dark:text-gray-100 font-medium">{formatDate(displayAnswer)}</span>
            </div>
            {timestamp}
          </div>
        );
      
      case 'ordinal':
      case 'nominal': {
        // Build a set of selected values for quick lookup
        const selectedValues = new Set(
          Array.isArray(displayAnswer)
            ? displayAnswer.map(v => String(v))
            : [String(displayAnswer)]
        );
        // Show all options with selected/unselected distinction
        if (question.options && Array.isArray(question.options)) {
          return (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
              <div className="grid gap-2">
                {question.options.map((option, idx) => {
                  const optionLabel = typeof option === 'object' ? (option.label || option.value || '') : option;
                  const optionValue = typeof option === 'object' ? (option.value !== undefined ? option.value : option.label) : option;
                  const isSelected = selectedValues.has(String(optionValue));
                  const accentColor = type === 'ordinal' ? 'cyan' : 'pink';
                  return (
                    <div key={idx} className={`flex items-center p-2 rounded-md ${isSelected ? `bg-${accentColor}-50 border border-${accentColor}-200` : 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700'}`}>
                      {isSelected ? (
                        <Check className={`w-4 h-4 text-${accentColor}-500 mr-3 flex-shrink-0`} />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 dark:text-gray-600 mr-3 flex-shrink-0" />
                      )}
                      <span className={isSelected ? 'font-medium text-gray-800 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>{optionLabel}</span>
                    </div>
                  );
                })}
              </div>
              {timestamp}
            </div>
          );
        }
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-primary-50 border border-primary-200">
              <div className="h-3 w-3 rounded-full bg-primary-500 mr-3"></div>
              <span className="font-medium text-gray-800 dark:text-gray-100">{typeof displayAnswer === 'object' ? (displayAnswer.label || displayAnswer.value) : resolveAnswerLabel(displayAnswer, question.options)}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      case 'dropdown': {
        const dropdownLabel = typeof displayAnswer === 'object' 
          ? (displayAnswer.label || displayAnswer.value || JSON.stringify(displayAnswer))
          : displayAnswer;
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20">
            <div className="flex items-center p-2 rounded-md bg-indigo-50 border border-indigo-200">
              <ChevronDown className="w-4 h-4 text-indigo-500 mr-2" />
              <span className="font-medium text-gray-800 dark:text-gray-100">{dropdownLabel}</span>
            </div>
            {timestamp}
          </div>
        );
      }
      default:
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20">
            {String(displayAnswer)}
            {timestamp}
          </div>
        );
    }
  };

  // Toggle expanded view for a question
  const toggleExpandQuestion = (questionId) => {
    if (expandedQuestionId === questionId) {
      setExpandedQuestionId(null);
    } else {
      setExpandedQuestionId(questionId);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-10 flex flex-col items-center max-w-md w-full"
        >
          <div className="relative w-20 h-20 mb-6">
            <svg className="absolute inset-0" viewBox="0 0 100 100">
              <circle 
                cx="50" cy="50" r="40" 
                stroke="#f3f4f6" 
                strokeWidth="8" 
                fill="none" 
              />
              <motion.circle 
                cx="50" cy="50" r="40" 
                stroke="#0d9488" 
                strokeWidth="8" 
                fill="none" 
                strokeLinecap="round"
                strokeDasharray="251.2"
                strokeDashoffset="251.2"
                animate={{ strokeDashoffset: 0 }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatType: "loop",
                  ease: "easeInOut" 
                }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <File className="w-8 h-8 text-primary-600" />
            </div>
          </div>
          <h3 className="text-xl font-medium text-gray-800 dark:text-gray-100 mb-2">Loading Survey</h3>
          <p className="text-gray-500 dark:text-gray-400 text-center">Retrieving comprehensive survey data</p>
          
          <div className="w-full mt-8">
            <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-primary-500 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 text-center mb-3">Unable to Load Survey</h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">{error}</p>
          <button 
            onClick={handleBack}
            className="w-full flex items-center justify-center px-4 py-3 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-md dark:shadow-gray-900/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Previous Page
          </button>
        </motion.div>
      </div>
    );
  }

  // Survey not found state
  if (!surveyDetails) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-amber-50 mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 text-center mb-3">Survey Not Found</h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">We couldn't locate the survey you're looking for. It may have been deleted or moved.</p>
          <button 
            onClick={handleBack}
            className="w-full flex items-center justify-center px-4 py-3 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-md dark:shadow-gray-900/20"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Previous Page
          </button>
        </motion.div>
      </div>
    );
  }

  // Filter questions based on active section
  const filteredQuestions = getFilteredQuestions();

  // Internal PKs for troubleshooting (URL fallbacks on participant > survey routes)
  const resolvedStudyIdForDebug =
    surveyDetails.study_id ??
    (studyId != null && studyId !== '' ? studyId : undefined) ??
    surveyDetails.metadata?.study_id;
  const resolvedSiteIdForDebug =
    surveyDetails.site_id ??
    (siteId != null && siteId !== '' ? siteId : undefined) ??
    surveyDetails.metadata?.site_id;
  // Business identifiers from API (Study.code, Site.site_code — same fields as site list / DB)
  const resolvedStudyCodeForDebug =
    surveyDetails.study_code ?? surveyDetails.metadata?.study_code;
  const resolvedSiteCodeForDebug =
    surveyDetails.site_code ?? surveyDetails.metadata?.site_code;

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Sticky header that appears on scroll */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: scrolled ? 1 : 0, y: scrolled ? 0 : -20 }}
        className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 z-20 shadow-md dark:shadow-gray-900/20 border-b border-gray-200 dark:border-gray-700"
        style={{ pointerEvents: scrolled ? 'auto' : 'none' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="mr-3 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate max-w-xl">
              {surveyDetails.survey_name}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
              <Share className="h-5 w-5" />
            </button>
            <button className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
              <Download className="h-5 w-5" />
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowActions(!showActions)}
                className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
              
              {showActions && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95, y: 5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-48 z-30"
                >
                  <div className="py-1">
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Download className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-400" />
                      Export PDF
                    </button>
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Printer className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-400" />
                      Print
                    </button>
                    <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <Share className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-400" />
                      Share
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Back button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Participant</span>
          </button>
        </div>
        
        {/* Survey header card */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden mb-6"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">
              {surveyDetails.survey_name}
            </h2>
          </div>
          
          {/* Info bar - light: primary tint; dark: neutral surface so text isn’t green-on-green */}
          <div className="px-6 py-4 bg-primary-50 text-primary-900 dark:bg-gray-800/90 dark:text-gray-100 dark:border-b dark:border-gray-700 text-sm flex flex-wrap gap-4 md:gap-6">
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Status:</span>
              <span className="capitalize text-primary-900 dark:text-gray-100">{surveyDetails.status.replace(/_/g, ' ')}</span>
              {surveyDetails.is_suspended && <SuspendedBadge className="ml-2" />}
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Started:</span>
              <span className="text-primary-900 dark:text-gray-100">{surveyDetails.openedAt ? formatDateTime(surveyDetails.openedAt) : 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Completed:</span>
              <span className="text-primary-900 dark:text-gray-100">{surveyDetails.completedAt ? formatDateTime(surveyDetails.completedAt) : 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-semibold mr-2 text-primary-900 dark:text-gray-400">Duration:</span>
              <span className="text-primary-900 dark:text-gray-100">
                {surveyDetails.duration_seconds 
                  ? formatDurationFromSeconds(surveyDetails.duration_seconds)
                  : surveyDetails.openedAt && surveyDetails.completedAt 
                    ? calculateDuration(surveyDetails.openedAt, surveyDetails.completedAt) 
                    : 'N/A'}
              </span>
            </div>
          </div>
          
          <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="rounded-full bg-primary-50 p-3 mr-4">
                <User className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Participant</div>
                <div className="text-gray-900 dark:text-gray-100 font-medium">
                  {/* Display human-readable participant ID if available, otherwise show 'Loading...' */}
                  {participantHumanId || 'Loading...'}
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="rounded-full bg-primary-50 p-3 mr-4">
                <Clock className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Schedule Type</div>
                <div className="text-gray-900 dark:text-gray-100 font-medium capitalize">
                  {(surveyDetails.metadata && surveyDetails.metadata.schedule_type
                    ? surveyDetails.metadata.schedule_type
                    : surveyDetails.schedule_type).replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Partial Completion Warning Banner */}
        {surveyDetails.is_suspended && surveyDetails.questions && surveyDetails.questions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-amber-50 border-l-4 border-amber-400 rounded-lg shadow-sm dark:shadow-gray-900/20 mb-6 p-4"
          >
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 mb-1">Partial Survey - Suspended Due to Participant Deactivation</h3>
                <p className="text-sm text-amber-800">
                  This survey was suspended when the participant was deactivated. The responses shown below were captured before suspension and are marked with Partial status. 
                  All responses include item-level timestamps. Data corrections can be handled through the Data Change Request (DCR) workflow.
                </p>
                <div className="mt-2 text-xs text-amber-700">
                  <span className="font-medium">Note:</span> Partial responses are excluded from scoring and endpoint derivation by default.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Responses Section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden mb-6"
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Responses</h2>
              <div className="ml-2 bg-primary-50 text-primary-700 text-xs font-medium rounded-full px-2 py-0.5">
                {surveyDetails.questions ? surveyDetails.questions.length : 0}
              </div>
              {surveyDetails.is_suspended && surveyDetails.questions && surveyDetails.questions.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Partial
                </span>
              )}
            </div>
            
            {surveyDetails.questions && surveyDetails.questions.length > 3 && (
              <div className="flex">
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 divide-x divide-gray-200 dark:divide-gray-700 overflow-hidden">
                  <button
                    onClick={() => setActiveSection('all')}
                    className={`px-3 py-1.5 transition-colors ${
                      activeSection === 'all'
                        ? 'bg-primary-500 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setActiveSection('text')}
                    className={`px-3 py-1.5 transition-colors ${
                      activeSection === 'text'
                        ? 'bg-primary-500 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Text
                  </button>
                  <button
                    onClick={() => setActiveSection('choice')}
                    className={`px-3 py-1.5 transition-colors ${
                      activeSection === 'choice'
                        ? 'bg-primary-500 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Choices
                  </button>
                  <button
                    onClick={() => setActiveSection('numeric')}
                    className={`px-3 py-1.5 transition-colors ${
                      activeSection === 'numeric'
                        ? 'bg-primary-500 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Numeric
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-0">
            {surveyDetails.questions && surveyDetails.questions.length > 0 && !((surveyDetails.status === 'open' || surveyDetails.status === 'pending') && !surveyDetails.has_partial_responses) ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Question Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Question Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Question
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Answer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <AnimatePresence mode="popLayout">
                      {filteredQuestions.map((question, index) => (
                        <React.Fragment key={question.id}>
                          <motion.tr 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${expandedQuestionId === question.id ? 'bg-gray-50 dark:bg-gray-900' : ''}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                              {/* FECOA-1447: show author-defined question code, falling back to presentation position */}
                              {question.code || `Q${surveyDetails.questions.indexOf(question) + 1}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getQuestionTypeColor(question.type)}`}>
                                {formatQuestionType(question.type)}
                              </span>
                              {question.is_required === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ml-1.5" aria-label="Optional question">
                                  Optional
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-1.5" aria-label="Required question">
                                  Required
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                              <div className="truncate" dangerouslySetInnerHTML={{ __html: question.text || '' }} />
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {getFormattedTableAnswer(question)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <button 
                                onClick={() => toggleExpandQuestion(question.id)}
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                              >
                                {expandedQuestionId === question.id ? 
                                  <ChevronUp className="h-4 w-4 text-primary-600" /> : 
                                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                }
                              </button>
                            </td>
                          </motion.tr>
                          {/* Expanded detail row */}
                          {expandedQuestionId === question.id && (
                            <motion.tr 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="bg-gray-50 dark:bg-gray-900"
                            >
                              <td colSpan={5} className="px-6 py-4">
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700">
                                  <div className="mb-3">
                                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Detailed Answer</h4>
                                  </div>
                                  {renderDetailedAnswer(question)}
                                </div>
                              </td>
                            </motion.tr>
                          )}
                        </React.Fragment>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-full shadow-md dark:shadow-gray-900/20 mb-4">
                  <MessageSquare className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                {surveyDetails.is_suspended ? (
                  <>
                    <h3 className="text-lg font-medium text-amber-900 mb-2">Survey Suspended</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center max-w-md">
                      This survey was suspended due to participant deactivation. No responses were recorded before suspension.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mt-2">
                      Any partial responses captured before suspension are saved with item-level timestamps and marked as Partial status.
                    </p>
                  </>
                ) : (surveyDetails.status === 'open' || surveyDetails.status === 'pending') ? (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {surveyDetails.status === 'open' ? 'Survey Is Open' : 'Survey Not Yet Started'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                      {surveyDetails.status === 'open' 
                        ? 'This survey window is currently open, but no responses have been recorded yet.'
                        : 'This survey is scheduled but not yet available to the participant.'}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Responses Available</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
                      This survey doesn't have any recorded responses.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Metadata/Debug section - unobtrusive toggle (shown whenever details load so Study/Site IDs are always available for troubleshooting) */}
        <div className="fixed bottom-12 right-4 z-10" style={{ position: 'fixed' }}>
            {/* Always visible toggle button */}
            <div className="flex items-center mb-2 justify-end">
              <button
                onClick={() => setShowDebugInfo(!showDebugInfo)}
                className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full flex items-center px-3 py-1.5 shadow-sm dark:shadow-gray-900/20 transition-colors"
                title="Toggle debug information"
              >
                <Info className="h-3.5 w-3.5 mr-1" />
                {showDebugInfo ? 'Hide' : 'Show'} Debug Info
              </button>
            </div>
            
            {/* Collapsible debug panel */}
            <AnimatePresence>
              {showDebugInfo && (
                <motion.div 
                  initial={{ opacity: 0, y: 10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: 10, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden border border-gray-200 dark:border-gray-700 max-w-md max-h-96 overflow-y-auto"
                >
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div className="flex items-center">
                      <File className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                      <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">Debug Information</h2>
                    </div>
                    <button
                      onClick={() => setShowDebugInfo(false)}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Core Survey Info */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Core Survey Info</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Completed Survey ID', value: surveyDetails.id },
                          { label: 'Survey ID', value: surveyDetails.survey_id },
                          { label: 'Window ID', value: surveyDetails.window_id },
                          { label: 'Survey Name', value: surveyDetails.survey_name },
                          { label: 'Status', value: surveyDetails.status },
                          { label: 'Is Suspended', value: surveyDetails.is_suspended },
                          { label: 'Has Partial Responses', value: surveyDetails.has_partial_responses },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Timing */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Timing</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Opened At', value: surveyDetails.openedAt },
                          { label: 'Completed At', value: surveyDetails.completedAt },
                          { label: 'Form Open Time', value: surveyDetails.form_open_time },
                          { label: 'Last Response Time', value: surveyDetails.last_response_time },
                          { label: 'Duration (seconds)', value: surveyDetails.duration_seconds },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Participant */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Participant</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Participant ID', value: surveyDetails.participant_id },
                          { label: 'Participant Human ID', value: surveyDetails.participant_human_id },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Study & Site — business codes (DB) + internal PKs for engineering */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Study & Site</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Study code', value: resolvedStudyCodeForDebug },
                          { label: 'Site code', value: resolvedSiteCodeForDebug },
                          { label: 'Study ID (internal)', value: resolvedStudyIdForDebug },
                          { label: 'Site ID (internal)', value: resolvedSiteIdForDebug },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Response Stats */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Response Stats</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { label: 'Total Questions', value: surveyDetails.questions?.length },
                          { label: 'Responses Count', value: surveyDetails.responses ? Object.keys(surveyDetails.responses).length : 0 },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                            <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
                            <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                              {value !== undefined && value !== null ? String(value) : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Additional Metadata */}
                    {surveyDetails.metadata && Object.keys(surveyDetails.metadata).length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 uppercase tracking-wide">Additional Metadata</h3>
                        <div className="grid grid-cols-1 gap-2">
                          {Object.entries(surveyDetails.metadata).map(([key, value]) => (
                            <div key={key} className="bg-gray-50 dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700 text-xs">
                              <div className="font-medium text-gray-500 dark:text-gray-400 mb-0.5 capitalize">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-gray-900 dark:text-gray-100 break-words font-mono">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
      </div>
    </div>
  );
};

export default CompletedSurveyDetails;